import { MaterialIcon } from '@/ui/material-icon'
import excelIcon from '@/assets/icons/excel.svg'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/tooltip'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'
import { useState } from 'react'
import styles from './ConfigureDataSourceSheet.module.scss'

type FieldRowProps = {
  label: string
  optional?: boolean
  type?: string
  options?: string[]
  placeholder?: string
  value: string
  onChange: (v: string) => void
  infoLabel: string
  disabled?: boolean
  fieldKey?: string
  error?: string
  /** Fires when a file is chosen (or cleared) on file inputs. */
  onFilePicked?: (file: File | null) => void
}

function getInputClassName(error?: string): string {
  return `${styles.textInput} ${error ? styles.inputError : ''}`
}

function isSelectType(type: string): boolean {
  return type === 'select' || type === 'matselect'
}

function isFileType(type: string, fieldKey?: string, label?: string): boolean {
  return type === 'file' || fieldKey === 'file_name' || label?.toLowerCase().includes('file') === true
}

const EMPTY_SELECT_VALUE = '__empty__'

function renderSelectInput(
  inputClassName: string,
  value: string,
  onChange: (v: string) => void,
  disabled: boolean | undefined,
  selectPlaceholder: string,
  options?: string[],
): JSX.Element {
  const selectValue = value || EMPTY_SELECT_VALUE
  return (
    <Select
      value={selectValue}
      onValueChange={next => onChange(next === EMPTY_SELECT_VALUE ? '' : next)}
      disabled={disabled}
    >
      <SelectTrigger className={inputClassName}>
        <SelectValue placeholder={selectPlaceholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={EMPTY_SELECT_VALUE}>{selectPlaceholder}</SelectItem>
        {(options ?? []).map(option => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function renderFileInput(
  disabled: boolean | undefined,
  onChange: (v: string) => void,
  onFilePicked: ((file: File | null) => void) | undefined,
  filePlaceholder: string,
  error?: string,
): JSX.Element {
  return (
    <label className={styles.fileInputLabel}>
      <Input
        type="file"
        accept=".csv,.tsv,.txt"
        className={`${styles.fileInput} ${styles.textInput}`}
        disabled={disabled}
        onChange={e => {
          const file = e.target.files?.[0] ?? null
          onChange(file?.name ?? '')
          onFilePicked?.(file)
        }}
      />
      <span className={`${styles.fileInputDisplay} ${error ? styles.inputError : ''}`}>{filePlaceholder}</span>
      <img src={excelIcon} alt="Excel" className={styles.fileInputIcon} />
    </label>
  )
}

type RenderTextInputArgs = {
  isPasswordField: boolean
  inputClassName: string
  resolvedInputType: string
  textPlaceholder: string
  normalizedInputName: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  isPasswordVisible: boolean
  onTogglePasswordVisibility: () => void
}

function renderTextInput({
  isPasswordField,
  inputClassName,
  resolvedInputType,
  textPlaceholder,
  normalizedInputName,
  value,
  onChange,
  disabled,
  isPasswordVisible,
  onTogglePasswordVisibility,
}: Readonly<RenderTextInputArgs>): JSX.Element {
  return (
    <div className={isPasswordField ? styles.passwordInputWrap : undefined}>
      <Input
        className={`${inputClassName} ${isPasswordField ? styles.passwordTextInput : ''}`.trim()}
        type={resolvedInputType}
        placeholder={textPlaceholder}
        name={normalizedInputName}
        value={value}
        onChange={e => onChange(e.target.value)}
        autoComplete={isPasswordField ? 'new-password' : 'off'}
        disabled={disabled}
      />
      {isPasswordField && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={styles.passwordToggle}
          onClick={onTogglePasswordVisibility}
          aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
          disabled={disabled}
        >
          <MaterialIcon name={isPasswordVisible ? 'visibility_off' : 'visibility'} size={18} />
        </Button>
      )}
    </div>
  )
}

export function FieldRow({
  label,
  optional,
  type = 'text',
  options,
  placeholder,
  value,
  onChange,
  infoLabel,
  disabled,
  fieldKey,
  error,
  onFilePicked,
}: Readonly<FieldRowProps>) {
  const inputClassName = getInputClassName(error)
  const isPasswordField = type === 'password' || fieldKey?.toLowerCase() === 'password'
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const selectPlaceholder = placeholder || `Select ${label}`
  const filePlaceholder = value || placeholder || `Choose ${label}`
  const textPlaceholder = placeholder || 'Input Value'
  const normalizedInputName = label.split(/\s+/).join('-').toLowerCase()
  let resolvedInputType = 'text'
  if (isPasswordField) {
    resolvedInputType = isPasswordVisible ? 'text' : 'password'
  }

  let inputControl = renderTextInput({
    isPasswordField,
    inputClassName,
    resolvedInputType,
    textPlaceholder,
    normalizedInputName,
    value,
    onChange,
    disabled,
    isPasswordVisible,
    onTogglePasswordVisibility: () => setIsPasswordVisible(prev => !prev),
  })
  if (isSelectType(type)) {
    inputControl = renderSelectInput(inputClassName, value, onChange, disabled, selectPlaceholder, options)
  } else if (isFileType(type, fieldKey, label)) {
    inputControl = renderFileInput(disabled, onChange, onFilePicked, filePlaceholder, error)
  }

  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>
        <span>
          {label}
          {optional && <span className={styles.optionalHint}> (optional)</span>}
        </span>

        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={styles.infoBtn} role="img" aria-label="Field info">
                <MaterialIcon name="info" size={16} />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{infoLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {inputControl}

      {error && <span className={styles.fieldError}>{error}</span>}
    </div>
  )
}
